import { useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { api, formatApiError } from "../lib/api";
import { KeyRound } from "lucide-react";

const inputCls =
  "w-full bg-[#0a0a0a] border border-[#262626] focus:border-white outline-none px-4 py-3 rounded-sm text-white transition-colors";

export default function ResetPasswordPage() {
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [done, setDone] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (password !== confirm) {
      setError("Passwords do not match.");
      return;
    }
    setLoading(true);
    try {
      await api.post("/auth/reset-password", { token, password });
      setDone(true);
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#050505] flex items-center justify-center px-4" data-testid="reset-password-page">
      <div className="w-full max-w-sm">
        <h1 className="font-display text-4xl uppercase tracking-tight text-white mb-1">EGNCY</h1>
        <p className="text-sm text-[#a3a3a3] mb-6">Choose a new password.</p>
        {!token ? (
          <div data-testid="reset-no-token" className="bg-[#121212] border border-[#262626] rounded-sm p-6 space-y-3">
            <p className="text-sm text-[#a3a3a3]">This reset link is missing its token.</p>
            <Link to="/forgot-password" className="text-sm text-white hover:underline">Request a new link</Link>
          </div>
        ) : done ? (
          <div data-testid="reset-done" className="bg-[#121212] border border-[#262626] rounded-sm p-6 space-y-3">
            <KeyRound className="w-8 h-8 text-green-400" />
            <p className="text-white font-semibold">Password updated</p>
            <p className="text-sm text-[#a3a3a3]">You can now log in with your new password.</p>
            <Link
              to="/login"
              data-testid="reset-go-login"
              className="inline-block bg-white text-black font-semibold px-6 py-2.5 rounded-sm hover:bg-gray-200 transition-colors text-sm"
            >
              Log in
            </Link>
          </div>
        ) : (
          <form onSubmit={submit} className="bg-[#121212] border border-[#262626] rounded-sm p-6 space-y-4">
            {error && (
              <div data-testid="reset-error" className="text-sm text-red-400 border border-red-500/40 bg-red-500/10 p-3 rounded-sm">{error}</div>
            )}
            <input
              data-testid="reset-password-input"
              type="password"
              required
              minLength={6}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="New password (min 6 characters)"
              className={inputCls}
            />
            <input
              data-testid="reset-confirm-input"
              type="password"
              required
              minLength={6}
              value={confirm}
              onChange={(e) => setConfirm(e.target.value)}
              placeholder="Confirm new password"
              className={inputCls}
            />
            <button
              data-testid="reset-submit"
              disabled={loading}
              className="w-full bg-white text-black font-semibold py-3 rounded-sm hover:bg-gray-200 disabled:opacity-60 transition-colors"
            >
              {loading ? "Updating…" : "Update password"}
            </button>
          </form>
        )}
      </div>
    </div>
  );
}
